import type { Artifact, ArtifactVersion } from "@rakazo/contracts";
import { attachmentDownloadName } from "@rakazo/core";
import { rpc } from "./rpc";

export type ArtifactTarget = { botId: string } | { groupId: string };

export function decodeArtifactBase64(contentBase64: string): Uint8Array {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

export async function fetchArtifactBytes(
  target: ArtifactTarget,
  artifactId: string,
): Promise<Uint8Array> {
  const artifact = await rpc.artifacts.get({ ...target, artifactId });
  return decodeArtifactBase64(artifact.contentBase64);
}

/** One artifact version by id, with its metadata and decoded bytes (artifact panel, PIV-36). */
export async function fetchArtifactById(
  artifactId: string,
): Promise<{ artifact: Artifact; bytes: Uint8Array }> {
  const { contentBase64, ...artifact } = await rpc.artifacts.getById({ artifactId });
  return { artifact, bytes: decodeArtifactBase64(contentBase64) };
}

/** All versions of the family an artifact belongs to, newest first. */
export function listArtifactVersions(familyId: string): Promise<ArtifactVersion[]> {
  return rpc.artifacts.listVersions({ familyId });
}

/** Saves bytes as a browser download under the cleaned original name; the bytes stay untouched. */
export function downloadArtifactBytes(name: string, mimeType: string, bytes: Uint8Array): void {
  const blob = new Blob([new Uint8Array(bytes)], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = attachmentDownloadName(name, mimeType);
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function downloadArtifact(
  target: ArtifactTarget,
  artifactId: string,
  name: string,
  mimeType: string,
): Promise<void> {
  const bytes = await fetchArtifactBytes(target, artifactId);
  downloadArtifactBytes(name, mimeType, bytes);
}
